import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CCard, CCardBody, CCardHeader, CFormInput, CCol, CRow, CButton, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody } from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilPlus } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import 'react-toastify/dist/ReactToastify.css';
import { cilSearch } from '@coreui/icons';
import debounce from 'lodash.debounce';
import PermissionsNoPage, { Permissions } from 'src/components/Permissions';

const page = 1;
const urlApi = '/users';
const urlRouter = '/users';

const role = localStorage.getItem('user_role');
const userData = JSON.parse(localStorage.getItem('user_data'));

export async function LoaderUsers() {
  let query = `page=${page}`;
  if (role == 'ROLE_MANAGER')
    query += `&accounts.manager.id=${userData.id}`;

  let res = await AxiosInstance.get(`${urlApi}?${query}`).then((response) => {
    return response;
  });
  return res;
}

const roleList = {
  ROLE_ADMIN: 'Адміністратор',
  ROLE_MANAGER: 'Менеджер',
  ROLE_CLIENT: 'Клієнт',
  ROLE_USER: 'Без ролі',
  ROLE_SITE: 'Користувач адмін-панелі',
  APP_USER: 'Користувач Додатку'
};

const Users = ({ user_data }) => {
  // user_data prop може бути null, якщо routes.js завантажився до логіну
  // (читає localStorage на рівні модуля). Fallback: перевіряємо role напряму.
  const userRoleLocal = localStorage.getItem('user_role');

  // if (!Permissions(user_data, 'Users', 'VIEW_USERS') && userRoleLocal !== 'ROLE_ADMIN') {
  //   return <PermissionsNoPage />;
  // }
  const { data } = useLoaderData();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rowCountState, setRowCountState] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [firstNameFilter, setFirstNameFilter] = useState('');
  const [personalAccountFilter, setPersonalAccountFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  // DataGrid використовує 0-індексацію сторінок, API — 1-індексацію (page + 1 при запиті)
  const [currentGridPage, setCurrentGridPage] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const initialRows = data?.['hydra:member'] ?? [];
    const initialTotal = data?.['hydra:totalItems'] ?? initialRows.length;
    setRows(initialRows);
    setRowCountState(initialTotal);
  }, [data]);

  const getRowsTable = useCallback((p = 1) => {
    let request = '';

    if (usernameFilter) {
      request += `&username=${encodeURIComponent(usernameFilter)}`;
    }
    if (firstNameFilter) {
      request += `&firstName=${encodeURIComponent(firstNameFilter)}`;
    }
    if (personalAccountFilter) {
      request += `&appPersonalAccounts.number=${encodeURIComponent(personalAccountFilter)}`;
    }
    if (emailFilter) {
      request += `&email=${encodeURIComponent(emailFilter)}`;
    }

    const requestId = ++requestIdRef.current;
    setShowLoader(true);
    AxiosInstance.get(`${urlApi}?page=${p}${request}`).then((response) => {
      if (requestId !== requestIdRef.current) return;
      setRows(response.data['hydra:member']);
      setRowCountState(response.data['hydra:totalItems']);
    }).finally(() => {
      if (requestId !== requestIdRef.current) return;
      setShowLoader(false);
    });
  },
    [usernameFilter, firstNameFilter, personalAccountFilter, emailFilter]
  );

  const handlePageChange = (page) => {
    setCurrentGridPage(page);
    getRowsTable(page + 1);
  };

  const debouncedSearch = useCallback(
    debounce(() => {
      getRowsTable(1);
    }, 700),
    [getRowsTable]
  );

  useEffect(() => {
    setCurrentGridPage(0);
    debouncedSearch();
    return debouncedSearch.cancel;
  }, [usernameFilter, firstNameFilter, personalAccountFilter, emailFilter, debouncedSearch]);

  const handleClearFilters = useCallback(() => {
    setUsernameFilter('');
    setFirstNameFilter('');
    setPersonalAccountFilter('');
    setEmailFilter('');
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    {
      field: 'username', headerName: 'Користувач', width: 250,
      renderCell: (params) => {
        return <Link to={`${urlRouter}/view/${params.row.id}`} style={{ textDecoration: "none", color: "rgba(0, 0, 0, 0.87)" }}>{params.row.username}</Link>;
      }
    },
    { field: 'firstName', headerName: 'ПІБ', width: 350 },
    { field: 'phone', headerName: 'Телефон', width: 170 },
    {
      field: 'email', headerName: 'Email', width: 240,
      valueGetter: (params) => params.row.email || '-'
    },
    {
      field: "role",
      headerName: "Роль",
      sortable: false,
      width: 250,
      renderCell: (params) => {
        let role = '';
        if (typeof params.row.roles == 'object')
          role = params.row.roles.map((e) => {
            if (e !== 'ROLE_USER')
              return roleList[e];
          });
        return role;
      }
    },
    { field: 'status', headerName: 'Статус', width: 120 },
    {
      field: "action",
      headerName: "Переглянути",
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 110,
      renderCell: (params) => {
        return (
          <Link
            to={`${urlRouter}/view/${params.row.id}`}
            className='cutom-view-icon '
          >
            <CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
          </Link>
        );
      }
    },
  ];

  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CAccordion flush>
            <CAccordionItem>
              <CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
              <CAccordionBody>
                <CRow className="my-3 mx-2">
                  <CCol md={9} className="me-auto">
                    <CRow>
                      <CCol md={3} className='filter-field'>
                        <CFormInput
                          placeholder="Користувач"
                          value={usernameFilter}
                          onChange={(e) => setUsernameFilter(e.target.value)}
                          autoComplete="off"
                        />
                      </CCol>
                      <CCol md={3} className='filter-field'>
                        <CFormInput
                          placeholder="ПІБ"
                          value={firstNameFilter}
                          onChange={(e) => setFirstNameFilter(e.target.value)}
                          autoComplete="off"
                        />
                      </CCol>
                      <CCol md={3} className='filter-field'>
                        <CFormInput
                          placeholder="Особовий рахунок"
                          value={personalAccountFilter}
                          onChange={(e) => setPersonalAccountFilter(e.target.value)}
                          autoComplete="off"
                        />
                      </CCol>
                      <CCol md={3} className='filter-field'>
                        <CFormInput
                          placeholder="Email"
                          value={emailFilter}
                          onChange={(e) => setEmailFilter(e.target.value)}
                          autoComplete="off"
                        />
                      </CCol>
                    </CRow>
                  </CCol>
                  <CCol md={3} style={{ textAlign: "end" }}>
                    <CButton color="secondary" variant="outline" onClick={handleClearFilters}>
                      Очистити фільтри
                    </CButton>
                  </CCol>
                </CRow>
              </CAccordionBody>
            </CAccordionItem>
          </CAccordion>

          <CCardHeader>
            <strong>Користувачі</strong> <small></small>
          </CCardHeader>
          <CCardBody>
            <p className="text-medium-emphasis small">
              <Link to={`${urlRouter}/create`}><CButton color="secondary"><CIcon icon={cilPlus} customClassName="nav-icon" height={15} />Створити</CButton></Link>
            </p>
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                page={currentGridPage}
                pageSize={30}
                rowsPerPageOptions={[30]}
                pagination
                rowCount={rowCountState}
                paginationMode="server"
                loading={showLoader}
                onPageChange={handlePageChange}
              />
            </div>
            <div className="d-flex justify-content-start mt-3">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Повернутись
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default Users;